import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

function readCompletedSession(storageKey, tokenStorageKey) {
  try {
    const completed = JSON.parse(sessionStorage.getItem(storageKey));
    const token = sessionStorage.getItem(tokenStorageKey);
    return token && completed?.token === token ? completed : null;
  } catch {
    return null;
  }
}

export default function useCompletedSessionExit({
  storageKey,
  exitUrl,
  tokenStorageKey = "onboardingToken",
}) {
  const { key } = useLocation();
  const [completedSession, setCompletedSession] = useState(() =>
    readCompletedSession(storageKey, tokenStorageKey),
  );
  const [completedTokenOnEntry] = useState(() => completedSession?.token);
  const [isRestored, setIsRestored] = useState(false);
  const token = sessionStorage.getItem(tokenStorageKey);
  const isCompleted = Boolean(token && completedSession?.token === token);
  // Allow the active success screen, but never reopen a completed form.
  const shouldExit = isRestored || (isCompleted && (
    completedSession.token === completedTokenOnEntry ||
    completedSession.locationKey !== key
  ));

  const completeSession = useCallback(() => {
    const currentToken = sessionStorage.getItem(tokenStorageKey);
    if (!currentToken) return;
    const completed = { token: currentToken, locationKey: key };
    sessionStorage.setItem(storageKey, JSON.stringify(completed));
    setCompletedSession(completed);
  }, [key, storageKey, tokenStorageKey]);

  useEffect(() => {
    if (shouldExit) window.location.replace(exitUrl);
  }, [exitUrl, shouldExit]);

  useEffect(() => {
    if (!isCompleted || shouldExit) return;
    const redirectToWebsite = () => window.location.replace(exitUrl);
    window.addEventListener("popstate", redirectToWebsite);
    // Ensure Back is observable even if the preceding entry is another website.
    window.history.pushState(window.history.state, "", window.location.href);
    return () => window.removeEventListener("popstate", redirectToWebsite);
  }, [exitUrl, isCompleted, shouldExit]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted && readCompletedSession(storageKey, tokenStorageKey)) {
        setIsRestored(true);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [storageKey, tokenStorageKey]);

  return { completeSession, shouldExit };
}
